import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Polls `/api/payments/status/{session_id}` until the backend reports `paid`
 * (or we hit max attempts). The playbook recommends ~2s cadence × ~5 attempts;
 * we go 2s × 15 to tolerate a slow webhook. */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const pollsLeft = useRef(15);

  useEffect(() => {
    if (!sid) { setStatus("missing"); return; }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
        if (r.data.payment_status === "failed" || r.data.payment_status === "expired") {
          setStatus(r.data.payment_status); return;
        }
      } catch { /* retry */ }
      pollsLeft.current -= 1;
      if (pollsLeft.current <= 0) { setStatus("slow"); return; }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [sid]);

  return (
    <div className="min-h-screen bg-[#0A1329] text-white flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-lg w-full rounded-2xl border border-white/10 bg-[#0F1A38] p-10 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="h-10 w-10 text-cyan-300 mx-auto animate-spin mb-4" />
            <h1 className="font-display font-bold text-2xl">Confirming your payment…</h1>
            <p className="text-slate-300 mt-2">Hold on, this usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="h-14 w-14 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-300" />
            </div>
            <h1 className="font-display font-bold text-3xl">You're all set.</h1>
            <p className="text-slate-300 mt-3 leading-relaxed">
              Your purchase is confirmed. Our team has been notified and will reach out shortly to set up your tenant and onboard your users.
              <strong className="block mt-2 text-white">No access has been granted yet.</strong>
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Link to="/"><Button className="bg-riskeye-cyan text-[#0A1329] hover:bg-cyan-300 font-semibold">Back to home</Button></Link>
              <Link to="/trust"><Button variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white">Visit trust centre</Button></Link>
            </div>
          </>
        )}
        {status === "slow" && (
          <>
            <Loader2 className="h-10 w-10 text-amber-300 mx-auto mb-4" />
            <h1 className="font-display font-bold text-2xl">Still working on it</h1>
            <p className="text-slate-300 mt-2">Stripe is taking a little longer than usual. We'll email you the moment it confirms.</p>
            <Link to="/" className="mt-5 inline-block"><Button variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white">Back to home</Button></Link>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <AlertCircle className="h-10 w-10 text-rose-300 mx-auto mb-4" />
            <h1 className="font-display font-bold text-2xl">Payment {status}</h1>
            <p className="text-slate-300 mt-2">No charge was made. You can try again or contact us.</p>
            <Link to="/pricing" className="mt-5 inline-block"><Button className="bg-riskeye-cyan text-[#0A1329] hover:bg-cyan-300 font-semibold">Back to pricing</Button></Link>
          </>
        )}
        {status === "missing" && (
          <>
            <AlertCircle className="h-10 w-10 text-rose-300 mx-auto mb-4" />
            <h1 className="font-display font-bold text-2xl">Session not found</h1>
            <Link to="/pricing" className="mt-5 inline-block"><Button className="bg-riskeye-cyan text-[#0A1329] hover:bg-cyan-300 font-semibold">Back to pricing</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
